import React from "react";
import { isSafeImageSrc } from "../../services/safe-image-src";
import { CHANNEL_LOGO_IMAGE_ATTRIBUTE } from "./channel-logo.shared";
import styles from "./channel-logo.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The logo tile — drawn by `channel-identity.tsx` in a grid row (GR1) and by `logo-grid.tsx` in a
 * Genre Pack (GP5, GP6), so it exists once rather than twice.
 */
export const CHANNEL_LOGO_SHAPES = ["circle", "square"] as const;
export type ChannelLogoShape = (typeof CHANNEL_LOGO_SHAPES)[number];

export const CHANNEL_LOGO_SIZES = ["small", "medium", "signature", "genre"] as const;
export type ChannelLogoSize = (typeof CHANNEL_LOGO_SIZES)[number];

export const CHANNEL_LOGO_SURFACES = ["tertiary", "signature", "genre", "genre-myfree", "artwork"] as const;
export type ChannelLogoSurface = (typeof CHANNEL_LOGO_SURFACES)[number];

/* Published value → class. A map rather than `styles[value]`, because `size` and `surface` publish
   the same two words (`signature`, `genre`) for two different things, and one shared class would
   make a size silently paint a fill. */
const SHAPE_CLASS: Record<ChannelLogoShape, string> = {
  circle: "circle",
  square: "square"
};

const SIZE_CLASS: Record<ChannelLogoSize, string> = {
  small: "sizeSmall",
  medium: "sizeMedium",
  signature: "sizeSignature",
  genre: "sizeGenre"
};

const SURFACE_CLASS: Record<ChannelLogoSurface, string> = {
  tertiary: "surfaceTertiary",
  signature: "surfaceSignature",
  genre: "surfaceGenre",
  "genre-myfree": "surfaceGenreMyFree",
  artwork: "surfaceArtwork"
};

/* Each size step's largest drawn edge in px (`channel-logo.module.css`). */
const IMAGE_EDGE: Record<string, number> = {
  sizeSmall: 32,
  sizeMedium: 48,
  sizeSignature: 80,
  sizeGenre: 80
};

/* Art under `/dtvassets/global/channel-icons/` paints its own white disc and shadow, so a round
   tile drops ours around it.
   ponytail: matched by URL because no "OV logo" flag reaches DDS; pass one through if that changes. */
const SELF_BADGED_ART = /\/dtvassets\/global\/channel-icons\//;

/* An unpublished value lands on the drawn default rather than on no class at all, which paints a
   tile with no size or no fill. */
function published<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export interface ChannelLogoProps {
  /** Absent or unsafe draws the "no logo" tile (GR8); a URL that then fails is swapped for prod's default logo by the enclosing ChannelLineup's fallback listener. */
  logoUrl?: string;
  /**
   * `circle` is a channel; `square` is a subscription's app tile, which Figma draws with a 16px
   * radius, a hairline rim and an inner highlight pair (`10212:2473`).
   */
  shape?: ChannelLogoShape;
  /**
   * The container step, each one a desktop/mobile pair read off a frame: `signature` 80/64, `genre`
   * 80/54 as a circle and 80/44 as a square. `medium` (48) and `small` (32) are in none of those
   * frames and are what a caller that states no size keeps.
   */
  size?: ChannelLogoSize;
  /**
   * The fill, which Figma also breakpoints — see the stylesheet, where each value carries its node
   * id and the one design question it raises.
   */
  surface?: ChannelLogoSurface;
  /** Layout only. */
  className?: string;
}

function ChannelLogoImpl({
  logoUrl,
  shape = "circle",
  size = "medium",
  surface = "tertiary",
  className
}: ChannelLogoProps) {
  const shapeClass = SHAPE_CLASS[published(shape, CHANNEL_LOGO_SHAPES, "circle")];
  const sizeClass = SIZE_CLASS[published(size, CHANNEL_LOGO_SIZES, "medium")];
  const surfaceClass = SURFACE_CLASS[published(surface, CHANNEL_LOGO_SURFACES, "tertiary")];
  /* One policy for every image sink — the same gate `subscription-row.tsx` and `signature-grid.tsx`
     put on theirs. */
  const safeLogo = isSafeImageSrc(logoUrl) ? logoUrl : "";

  return (
    <span
      aria-hidden="true"
      className={[
        styles.tile,
        styles[shapeClass],
        styles[sizeClass],
        styles[surfaceClass],
        className
      ]
        .filter(Boolean)
        .join(" ")}
      /* The stylesheet drops our disc for this until the fallback listener marks the image, so React
         stays the only writer of the attribute. */
      data-cl-self-badged={shapeClass === "circle" && SELF_BADGED_ART.test(safeLogo) ? "" : undefined}
    >
      {/* Only without a logo: under a loaded one its outline shows through the art's transparent
          areas. */}
      {safeLogo === "" && (
        <svg className={styles.placeholder} focusable="false" viewBox="0 0 32 32">
          {/* A screen, not a letter: the tile stands in for channels whose name
              is already on the row, so an initial would be a second label. */}
          <rect
            fill="none"
            height="15"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.5"
            width="21"
            x="5.5"
            y="8.5"
          />
          <path d="M12 24.5h8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
        </svg>
      )}
      {/* `alt=""` is load-bearing, not an oversight — see the header. The
          width/height are the step's largest tile: an aspect ratio before the
          bytes arrive, the CSS still sets the drawn size. Keyed on the source so
          a new logo gets a fresh element, never the old one's fallback mark. */}
      {safeLogo !== "" && (
        <img
          key={safeLogo}
          alt=""
          className={styles.image}
          {...{ [CHANNEL_LOGO_IMAGE_ATTRIBUTE]: "" }}
          decoding="async"
          height={IMAGE_EDGE[sizeClass]}
          loading="lazy"
          src={safeLogo}
          width={IMAGE_EDGE[sizeClass]}
        />
      )}
    </span>
  );
}
export const ChannelLogo = withSafeRender(ChannelLogoImpl, "ChannelLogo");

export default ChannelLogo;
